import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Tv, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/api';
import { Logo } from '@/components/common/Logo';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';

const ActivateTVPage = () => {
  const [searchParams] = useSearchParams();
  const { viewer } = useViewerAuth();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error, need-login
  const [errorMsg, setErrorMsg] = useState('');

  // Pre-fill code from URL param
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode) setCode(urlCode.toUpperCase());
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    if (!viewer) {
      setStatus('need-login');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const res = await api.tvActivate(code.trim().toUpperCase());
      if (res.data.success) {
        setStatus('success');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4" data-testid="activate-tv-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/">
            <Logo className="h-12 w-auto" />
          </Link>
        </div>

        {/* SUCCESS STATE */}
        {status === 'success' && (
          <div className="text-center bg-gray-900 border border-green-500/30 rounded-2xl p-8" data-testid="activate-success">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">TV Connected!</h2>
            <p className="text-gray-400 text-sm mb-6">
              Your TV is now signed in as <span className="text-white font-medium">{viewer?.name || viewer?.email}</span>. You can close this page.
            </p>
            <Link to="/">
              <Button className="bg-sky-500 hover:bg-sky-600 w-full">Back to Home</Button>
            </Link>
          </div>
        )}

        {/* NEED LOGIN STATE */}
        {status === 'need-login' && (
          <div className="text-center bg-gray-900 border border-amber-500/30 rounded-2xl p-8" data-testid="activate-need-login">
            <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Sign In First</h2>
            <p className="text-gray-400 text-sm mb-6">
              You need to be signed in to activate your TV. Sign in and come back to this page.
            </p>
            <Link to={`/signup?redirect=/activate?code=${code}`}>
              <Button className="bg-sky-500 hover:bg-sky-600 w-full" data-testid="activate-signin-btn">Sign In / Create Account</Button>
            </Link>
          </div>
        )}

        {/* CODE ENTRY FORM */}
        {(status === 'idle' || status === 'submitting' || status === 'error') && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8" data-testid="activate-form">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-sky-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Tv className="w-7 h-7 text-sky-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">Activate Your TV</h2>
              <p className="text-gray-400 text-sm">
                Enter the code shown on your TV screen
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="bg-gray-800 border-gray-700 text-white text-center text-2xl tracking-[0.3em] font-bold py-6 placeholder:text-lg placeholder:tracking-normal placeholder:font-normal"
                  maxLength={6}
                  autoFocus
                  data-testid="activate-code-input"
                />
              </div>

              {status === 'error' && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="activate-error">
                  <p className="text-red-400 text-sm text-center">{errorMsg}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3"
                disabled={code.length < 6 || status === 'submitting'}
                data-testid="activate-submit-btn"
              >
                {status === 'submitting' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating...</>
                ) : (
                  'Activate TV'
                )}
              </Button>
            </form>

            {!viewer && (
              <p className="text-gray-500 text-xs text-center mt-4">
                Not signed in? <Link to="/signup" className="text-sky-400 hover:text-sky-300 underline">Sign in first</Link>, then come back here.
              </p>
            )}
          </div>
        )}

        <p className="text-gray-600 text-xs text-center mt-6">
          Having trouble? Make sure the code on your TV matches what you entered.
        </p>
      </div>
    </div>
  );
};

export default ActivateTVPage;
